import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';

// ─── helpers ────────────────────────────────────────────────────────────────

const Toast = ({ msg, onClose }) => (
  <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-xl animate-fade-in">
    <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    <span className="text-sm font-medium">{msg}</span>
    <button onClick={onClose} className="ml-2 text-gray-400 hover:text-white">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const useToast = () => {
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  return { toast, showToast };
};

// ─── Progress bar ────────────────────────────────────────────────────────────

const ProgressBar = ({ value }) => (
  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
    <div
      className="h-2 rounded-full transition-all duration-500"
      style={{ width: `${value}%`, background: 'linear-gradient(90deg,#20C997,#003479)' }}
    />
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const StudentSelfPaced = () => {
  const { toast, showToast } = useToast();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toggling, setToggling] = useState(null); // module id being toggled

  // ── fetch all courses ──────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/api/selfpaced');
      setCourses(res.data || []);
    } catch {
      setError('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── fetch course detail (with progress) ────────────────────────────────────
  const fetchDetail = useCallback(async (courseId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/api/selfpaced/${courseId}`);
      setDetail(res.data);
    } catch {
      showToast('Failed to load course details');
    } finally {
      setDetailLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleSelectCourse = (course) => {
    setSelected(course);
    fetchDetail(course.id);
  };

  // ── toggle module complete ──────────────────────────────────────────────────
  const handleToggleModule = async (mod) => {
    setToggling(mod.id);
    try {
      if (mod.is_completed) {
        await api.delete(`/api/selfpaced/${selected.id}/modules/${mod.id}/progress`);
        showToast('Module marked incomplete');
      } else {
        await api.post(`/api/selfpaced/${selected.id}/modules/${mod.id}/progress`);
        showToast('Module completed!');
      }
      // Refresh detail and course list (for progress bar update in list)
      await fetchDetail(selected.id);
      await fetchCourses();
    } catch {
      showToast('Failed to update progress');
    } finally {
      setToggling(null);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="skeleton h-8 w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-36 rounded-2xl" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast msg={toast} onClose={() => {}} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(32,201,151,0.12)' }}>
            <svg className="w-5 h-5" style={{ color: '#20C997' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Self-Paced Courses</h1>
            <p className="text-sm text-gray-500">
              {courses.length} course{courses.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {courses.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No courses available yet</p>
              <p className="text-xs text-gray-400 mt-1">Your instructor will grant you access to self-paced courses</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* ── Course cards list ── */}
            <div className="lg:col-span-2 space-y-3">
              {courses.map((course) => {
                const pct = course.module_count > 0
                  ? Math.round(((course.completed_count || 0) / course.module_count) * 100)
                  : 0;
                return (
                  <div
                    key={course.id}
                    onClick={() => handleSelectCourse(course)}
                    className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all hover:shadow-sm ${
                      selected?.id === course.id
                        ? 'border-[#20C997] shadow-sm ring-1 ring-[#20C997]/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">{course.title}</p>
                    {course.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{course.description}</p>
                    )}
                    <div className="mt-3 space-y-1.5">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{course.module_count} module{course.module_count !== 1 ? 's' : ''}</span>
                        <span className="font-medium" style={{ color: '#20C997' }}>{pct}%</span>
                      </div>
                      <ProgressBar value={pct} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Module viewer ── */}
            <div className="lg:col-span-3">
              {!selected ? (
                <div className="bg-white rounded-2xl border border-dashed border-gray-300 h-64 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                    </svg>
                    <p className="text-sm font-medium">Select a course to view modules</p>
                  </div>
                </div>
              ) : detailLoading ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                  {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}
                </div>
              ) : detail ? (
                <div className="space-y-4">
                  {/* Course header with progress */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900">{detail.title}</h2>
                        {detail.description && (
                          <p className="text-sm text-gray-500 mt-1">{detail.description}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-2xl font-bold" style={{ color: '#20C997' }}>
                          {detail.progress_percentage}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {detail.completed_count}/{detail.module_count} done
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <ProgressBar value={detail.progress_percentage} />
                    </div>
                  </div>

                  {/* Modules list */}
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900">Modules</h3>
                    </div>

                    {!detail.modules || detail.modules.length === 0 ? (
                      <div className="py-10 text-center text-gray-400">
                        <p className="text-sm">No modules yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {detail.modules.map((mod, idx) => (
                          <div
                            key={mod.id}
                            className={`flex items-center gap-4 px-5 py-4 transition-colors ${
                              mod.is_completed ? 'bg-emerald-50/40' : 'hover:bg-gray-50/60'
                            }`}
                          >
                            {/* Completion toggle */}
                            <button
                              onClick={() => handleToggleModule(mod)}
                              disabled={toggling === mod.id}
                              className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                mod.is_completed
                                  ? 'border-[#20C997] bg-[#20C997]'
                                  : 'border-gray-300 hover:border-[#20C997]'
                              } ${toggling === mod.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                            >
                              {mod.is_completed && (
                                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>

                            {/* Module number */}
                            <div
                              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                              style={{ background: mod.is_completed ? '#20C997' : '#003479' }}
                            >
                              {idx + 1}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${mod.is_completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                {mod.title}
                              </p>
                              {mod.is_completed && (
                                <p className="text-xs text-[#20C997] font-medium mt-0.5">Completed</p>
                              )}
                            </div>

                            {/* Watch button */}
                            <a
                              href={mod.youtube_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-2 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity flex-shrink-0"
                              style={{ background: mod.is_completed ? '#20C997' : '#003479' }}
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                              Watch
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Completion message */}
                  {detail.module_count > 0 && detail.completed_count === detail.module_count && (
                    <div className="rounded-2xl p-5 text-center" style={{ background: 'linear-gradient(135deg,rgba(32,201,151,0.12),rgba(0,52,121,0.08))' }}>
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#20C997' }}>
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="font-bold text-gray-900 text-lg">Course Completed!</p>
                      <p className="text-sm text-gray-500 mt-1">You've finished all modules in this course.</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentSelfPaced;
